import React, { useState } from 'react';
import { Layout, Menu, Button, message, Modal, Form, Input, Typography, Alert, Space, Tag } from 'antd';
import { useNavigate, useLocation, Outlet } from 'react-router-dom';
import useSiteSettings from '../hooks/useSiteSettings';
import {
  DashboardOutlined,
  UserOutlined,
  TeamOutlined,
  FileTextOutlined,
  QuestionCircleOutlined,
  EditOutlined,
  BarChartOutlined,
  LogoutOutlined,
  ApartmentOutlined,
  AuditOutlined,
  SolutionOutlined,
  LockOutlined,
  SettingOutlined,
  SafetyCertificateOutlined,
  StarOutlined,
  ReadOutlined,
  RobotOutlined,
  CopyOutlined,
  CheckOutlined,
} from '@ant-design/icons';
import { useAuth } from '../utils/auth';
import { changeAdminPassword, getMcpKey, generateMcpKey } from '../api/admin';

const { Header, Sider, Content } = Layout;
const { Text, Paragraph, Title } = Typography;

const AdminLayout = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();
  const siteSettings = useSiteSettings();
  const headerTitle = siteSettings?.title || siteSettings?.name || '管理后台';
  const logoUrl = siteSettings?.logo || '';
  const [pwdModalVisible, setPwdModalVisible] = useState(false);
  const [pwdForm] = Form.useForm();
  const [changingPwd, setChangingPwd] = useState(false);
  const [aiModalVisible, setAiModalVisible] = useState(false);
  const [copiedKey, setCopiedKey] = useState(null);
  const [mcpApiKey, setMcpApiKey] = useState(null);
  const [generatingKey, setGeneratingKey] = useState(false);

  const mcpBaseUrl = window.location.origin + '/api/mcp';

  const handleCopy = async (text, key) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedKey(key);
      message.success('已复制到剪贴板');
      setTimeout(() => setCopiedKey(null), 1500);
    } catch (error) {
      message.error('复制失败，请手动复制');
    }
  };

  // 打开弹窗时加载已有 API Key
  const openAiModal = async () => {
    setAiModalVisible(true);
    try {
      const res = await getMcpKey();
      if (res.code === 200) {
        setMcpApiKey(res.data.key);
      }
    } catch (error) {
      // 加载失败不阻塞弹窗
    }
  };

  // 实际生成/重置 API Key
  const doGenerateMcpKey = async () => {
    setGeneratingKey(true);
    try {
      const res = await generateMcpKey();
      if (res.code === 200) {
        setMcpApiKey(res.data.key);
        message.success('API Key 已生成');
      }
    } catch (error) {
      message.error(error.message || '生成失败');
    } finally {
      setGeneratingKey(false);
    }
  };

  // 生成/重置 API Key（已有 Key 时先确认，防止误操作失效）
  const handleGenerateMcpKey = () => {
    if (mcpApiKey) {
      Modal.confirm({
        title: '重新生成 API Key',
        content: '注意：旧的 API Key 和 AI 连接器已失效，请在客户端更新配置。',
        okText: '确认重新生成',
        cancelText: '取消',
        onOk: doGenerateMcpKey,
      });
    } else {
      doGenerateMcpKey();
    }
  };

  // API Key 对应的 JSON 配置
  const mcpKeyJsonConfig = mcpApiKey ? `{\n  "mcpServers": {\n    "质量学习平台": {\n      "url": "${mcpBaseUrl}",\n      "headers": {\n        "Authorization": "Bearer ${mcpApiKey}"\n      }\n    }\n  }\n}` : '';

  const handleLogout = () => {
    logout();
    message.success('已退出登录');
    navigate('/admin/login');
  };

  const handleChangePassword = async () => {
    try {
      const values = await pwdForm.validateFields();
      setChangingPwd(true);
      const res = await changeAdminPassword({
        old_password: values.old_password,
        new_password: values.new_password
      });
      if (res.code === 200) {
        message.success('密码修改成功');
        setPwdModalVisible(false);
        pwdForm.resetFields();
      } else {
        message.error(res.message || '修改失败');
      }
    } catch (error) {
      message.error(error.message || '修改失败');
    } finally {
      setChangingPwd(false);
    }
  };

  const menuItems = [
    {
      key: '/admin/statistics',
      icon: <DashboardOutlined />,
      label: '数据看板',
    },
    {
      key: '/admin/promises',
      icon: <SolutionOutlined />,
      label: '学员承诺',
    },
    {
      key: 'group-content',
      icon: <ReadOutlined />,
      label: '内容管理',
      children: [
        { key: '/admin/contents', icon: <FileTextOutlined />, label: '内容管理' },
        { key: '/admin/rating-items', icon: <StarOutlined />, label: '评分项管理' },
        { key: '/admin/questions', icon: <QuestionCircleOutlined />, label: '找茬题库' },
        { key: '/admin/fillblanks', icon: <EditOutlined />, label: '完形填空' },
      ],
    },
    {
      key: 'group-users',
      icon: <TeamOutlined />,
      label: '学员管理',
      children: [
        { key: '/admin/users', icon: <UserOutlined />, label: '用户管理' },
        { key: '/admin/departments', icon: <ApartmentOutlined />, label: '部门管理' },
      ],
    },
    {
      key: '/admin/login-logs',
      icon: <AuditOutlined />,
      label: '登录日志',
    },
    {
      key: 'group-settings',
      icon: <SettingOutlined />,
      label: '系统设置',
      children: [
        { key: '/admin/settings', icon: <SettingOutlined />, label: '网站设置' },
        { key: '/admin/security', icon: <SafetyCertificateOutlined />, label: '安全设置' },
      ],
    },
  ];

  // 自动展开包含当前路由的分组
  const getOpenKeys = () => {
    for (const item of menuItems) {
      if (item.children) {
        for (const child of item.children) {
          if (child.key === location.pathname) return [item.key];
        }
      }
    }
    return [];
  };

  // 从菜单提取 path → 页面信息映射，用于顶部标题随路由变化（含图标）
  const pageNameMap = {};
  menuItems.forEach(item => {
    if (item.children) {
      item.children.forEach(child => {
        pageNameMap[child.key] = { label: child.label, icon: child.icon };
      });
    } else {
      pageNameMap[item.key] = { label: item.label, icon: item.icon };
    }
  });
  const currentPage = pageNameMap[location.pathname];
  const currentPageTitle = currentPage?.label || headerTitle;
  const currentPageIcon = currentPage?.icon || null;

  const [openKeys, setOpenKeys] = useState(getOpenKeys());

  const handleMenuClick = ({ key }) => {
    navigate(key);
  };

  return (
    <Layout style={{ minHeight: '100vh' }}>
      <Sider
        theme="dark"
        width={200}
        style={{
          boxShadow: '2px 0 8px rgba(0,0,0,0.15)',
          position: 'fixed',
          left: 0,
          top: 0,
          bottom: 0,
          zIndex: 100,
          margin: 10,
          borderRadius: 12,
          height: 'calc(100vh - 20px)',
          overflow: 'hidden',
        }}
      >
        <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
          <div style={{
            height: '64px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            borderBottom: '1px solid rgba(255,255,255,0.1)',
            padding: '0 16px',
            flexShrink: 0,
          }}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              color: 'white',
              fontWeight: 'bold',
              gap: 8
            }}>
              {logoUrl && (
                <img
                  src={`/${logoUrl}`}
                  alt="Logo"
                  style={{ height: 28, objectFit: 'contain' }}
                />
              )}
              <span style={{ fontSize: 16, whiteSpace: 'nowrap' }}>管理后台</span>
            </div>
          </div>
          <div style={{ flex: 1, overflow: 'auto' }}>
            <Menu
              theme="dark"
              mode="inline"
              selectedKeys={[location.pathname]}
              openKeys={openKeys}
              onOpenChange={setOpenKeys}
              items={menuItems}
              onClick={handleMenuClick}
              style={{ marginTop: '10px' }}
            />
          </div>
          <div style={{
            borderTop: '1px solid rgba(255,255,255,0.1)',
            flexShrink: 0,
            padding: '4px 0',
          }}>
            <Menu
              theme="dark"
              mode="inline"
              selectable={false}
              items={[{
                key: 'logout',
                icon: <LogoutOutlined />,
                label: '退出登录',
                onClick: handleLogout,
              }]}
            />
          </div>
        </div>
      </Sider>
      <Layout style={{ marginLeft: 210 }}>
        <Header style={{
          padding: '0 24px',
          background: 'rgba(255,255,255,0.75)',
          backdropFilter: 'blur(12px)',
          WebkitBackdropFilter: 'blur(12px)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
          position: 'sticky',
          top: 10,
          zIndex: 99,
          margin: 10,
          borderRadius: 12,
          width: 'calc(100% - 20px)',
        }}>
          <div style={{ fontSize: '18px', fontWeight: '500', display: 'flex', alignItems: 'center', gap: 8 }}>
            {currentPageIcon}
            <span>{currentPageTitle}</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
            <Button
              type="link"
              icon={<RobotOutlined />}
              onClick={openAiModal}
            >
              AI连接器
            </Button>
            <Button
              type="link"
              icon={<UserOutlined />}
              onClick={() => setPwdModalVisible(true)}
            >
              {user?.name || '管理员'}
            </Button>
          </div>
        </Header>
        <Content style={{
          margin: 10,
          minHeight: 'calc(100vh - 96px)',
          background: '#f5f5f5',
        }}>
          <Outlet />
        </Content>
      </Layout>

      {/* AI 连接器配置弹窗 */}
      <Modal
        title="AI 连接器配置"
        open={aiModalVisible}
        onCancel={() => setAiModalVisible(false)}
        footer={<Button onClick={() => setAiModalVisible(false)}>关闭</Button>}
        width={640}
      >
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
          message="将本平台接入你的AI，便捷分析统计"
        />

        <Title level={5}>1. 服务器地址（自动获取当前域名）</Title>
        <Space style={{ marginBottom: 16 }} wrap>
          <Text code>{mcpBaseUrl}</Text>
          <Button
            size="small"
            icon={copiedKey === 'url' ? <CheckOutlined /> : <CopyOutlined />}
            onClick={() => handleCopy(mcpBaseUrl, 'url')}
          >
            {copiedKey === 'url' ? '已复制' : '复制'}
          </Button>
        </Space>

        <Title level={5}>2. API Key（推荐，长期有效）</Title>
        {mcpApiKey ? (
          <div style={{ marginBottom: 8 }}>
            <Space wrap>
              <Text code>{mcpApiKey}</Text>
              <Button
                size="small"
                icon={copiedKey === 'apiKey' ? <CheckOutlined /> : <CopyOutlined />}
                onClick={() => handleCopy(mcpApiKey, 'apiKey')}
              >
                {copiedKey === 'apiKey' ? '已复制' : '复制'}
              </Button>
            </Space>
            <div style={{ marginTop: 8 }}>
              <Text type="secondary">填入 AI 客户端的 Token 输入框即可。</Text>
            </div>
          </div>
        ) : (
          <div style={{ marginBottom: 8 }}>
            <Text type="secondary">尚未生成 API Key</Text>
          </div>
        )}
        <Button
          size="small"
          loading={generatingKey}
          icon={<RobotOutlined />}
          onClick={handleGenerateMcpKey}
        >
          {mcpApiKey ? '重新生成' : '生成 API Key'}
        </Button>

        <Title level={5} style={{ marginTop: 24 }}>3. 配置示例（JSON）</Title>
        {mcpKeyJsonConfig ? (
          <Paragraph style={{ marginBottom: 16 }}>
            <pre style={{
              background: '#f6f6f6',
              padding: 12,
              borderRadius: 8,
              overflow: 'auto',
              fontSize: 12,
              lineHeight: 1.6,
              margin: 0
            }}>
{mcpKeyJsonConfig}
            </pre>
          </Paragraph>
        ) : (
          <Text type="secondary">生成 API Key 后自动生成</Text>
        )}

        <Alert
          type="warning"
          showIcon
          style={{ marginTop: 16 }}
          message="注意：重新生成后旧 Key 立即失效"
        />
      </Modal>

      {/* 修改密码弹窗 */}
      <Modal
        title="修改密码"
        open={pwdModalVisible}
        onOk={handleChangePassword}
        onCancel={() => {
          setPwdModalVisible(false);
          pwdForm.resetFields();
        }}
        confirmLoading={changingPwd}
        okText="确认修改"
        cancelText="取消"
      >
        <Form
          form={pwdForm}
          layout="vertical"
          style={{ marginTop: 16 }}
        >
          <Form.Item
            name="old_password"
            label="旧密码"
            rules={[{ required: true, message: '请输入旧密码' }]}
          >
            <Input.Password prefix={<LockOutlined />} placeholder="请输入旧密码" />
          </Form.Item>
          <Form.Item
            name="new_password"
            label="新密码"
            rules={[
              { required: true, message: '请输入新密码' },
              { min: 6, message: '密码不能少于6位' }
            ]}
          >
            <Input.Password prefix={<LockOutlined />} placeholder="请输入新密码（至少6位）" />
          </Form.Item>
          <Form.Item
            name="confirm_password"
            label="确认新密码"
            rules={[
              { required: true, message: '请确认新密码' },
              ({ getFieldValue }) => ({
                validator(_, value) {
                  if (!value || getFieldValue('new_password') === value) {
                    return Promise.resolve();
                  }
                  return Promise.reject(new Error('两次输入的密码不一致'));
                },
              }),
            ]}
          >
            <Input.Password prefix={<LockOutlined />} placeholder="请再次输入新密码" />
          </Form.Item>
        </Form>
      </Modal>
    </Layout>
  );
};

export default AdminLayout;
